// components/dashboard/DashboardHeaderAdmin.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, BellRing, Mail, LogOut, User, Settings, ChevronDown } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import SearchBar from './SearchBar';
import useGlobale from './global';

const Header = ({ isSidebarOpen, setIsSidebarOpen, user }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const location = useLocation();
    const dropdownRef = useRef(null);
    const { logout } = useGlobale();

    // Fermer le dropdown quand on clique ailleurs
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Si le dropdown est ouvert et qu'on clique en dehors du bouton et du dropdown
            if (isDropdownOpen && 
                dropdownRef.current && 
                !dropdownRef.current.contains(event.target) &&
                !event.target.closest('[data-dropdown-menu]')) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    // Simuler le chargement des pages
    useEffect(() => {
        setIsLoading(true);
        setLoadingProgress(0);
        
        // Animation de progression
        const progressInterval = setInterval(() => {
            setLoadingProgress(prev => {
                if (prev >= 90) {
                    clearInterval(progressInterval);
                    return 90;
                }
                return prev + Math.random() * 30;
            });
        }, 100);

        // Finaliser le chargement
        const finishLoading = setTimeout(() => {
            setLoadingProgress(100);
            setTimeout(() => {
                setIsLoading(false);
                setLoadingProgress(0);
            }, 200);
        }, 800);

        return () => {
            clearInterval(progressInterval);
            clearTimeout(finishLoading);
        };
    }, [location.pathname]);

    return (
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
            <div className="px-4 lg:px-6">
                <div className="flex items-center justify-between h-16">
                    {/* Bouton menu mobile */}
                    <button
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        className="lg:hidden p-2 rounded-lg hover:bg-orange-50 transition-colors"
                    >
                        {isSidebarOpen ? (
                            <X className="w-6 h-6 text-orange-600" />
                        ) : (
                            <Menu className="w-6 h-6 text-orange-600" />
                        )}
                    </button>

                    {/* Barre de recherche */}
                    <div className="hidden md:flex ml-4 flex-1 max-w-xl">
                        <SearchBar />
                    </div>

                    {/* Actions à droite */}
                    <div className="flex items-center space-x-2">
                        {/* Notifications */}
                        <button className="p-2 rounded-lg hover:bg-gray-50 relative transition-colors">
                            <BellRing className="w-5 h-5 text-gray-600" />
                            <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full"></span>
                        </button>
                        
                        {/* Messages */}
                        <button className="p-2 rounded-lg hover:bg-gray-50 transition-colors">
                            <Mail className="w-5 h-5 text-gray-600" />
                        </button>
                        
                        {/* Divider */}
                        <div className="w-px h-6 bg-gray-200 mx-2"></div>
                        
                        {/* User Dropdown */}
                        <div className="relative" ref={dropdownRef}>
                            <button 
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
                                    {user?.photo_url ? (
                                        <img
                                            src={user.photo_url}
                                            alt="Profile"
                                            className="w-full h-full object-cover rounded-full"
                                        />
                                    ) : (
                                        <User className="w-4 h-4 text-white" />
                                    )}
                                </div>
                                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Dropdown Portal - en dehors du flux normal */}
            {isDropdownOpen && (
                <div 
                    data-dropdown-menu
                    className="fixed top-16 right-4 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[9999]"
                    style={{ 
                        position: 'fixed',
                        top: '4rem',
                        right: '1rem'
                    }}
                >
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">
                            {user?.first_name && user?.last_name 
                                ? `${user.first_name} ${user.last_name}`
                                : 'Utilisateur'
                            }
                        </p>
                        <p className="text-xs text-gray-500">{user?.email || 'Email'}</p>
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">
                            Administrateur
                        </span>
                    </div>

                    {/* Menu Items */}
                    <button 
                        onClick={() => {
                            console.log('Mon profil cliqué');
                            setIsDropdownOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <User className="w-4 h-4 mr-3 text-gray-400" />
                        Mon Profil
                    </button>
                    
                    <button 
                        onClick={() => {
                            console.log('Paramètres cliqué');
                            setIsDropdownOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <Settings className="w-4 h-4 mr-3 text-gray-400" />
                        Paramètres
                    </button>

                    <div className="border-t border-gray-100 mt-1 pt-1">
                        <button 
                            onClick={(e) => {
                                e.preventDefault();
                                console.log('Déconnexion cliquée');
                                setIsDropdownOpen(false);
                                // Délai pour permettre la fermeture du dropdown
                                setTimeout(() => {
                                    logout();
                                }, 100);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                            <LogOut className="w-4 h-4 mr-3" />
                            Déconnexion
                        </button>
                    </div>
                </div>
            )}

            {/* Barre de recherche mobile */}
            <div className="md:hidden px-4 pb-3 pt-2 border-t border-gray-100">
                <SearchBar />
            </div>
        </header>
    );
};

export default Header;